import type { FC } from 'react'

import { UpsellBlock, UpsellLandingPage } from 'generated/resources/datatable.graphql'

import { Box } from '@factorialco/deprecated-design-system'

import { GlpButtonBlock } from './GlpButtonBlock'
import { GlpCardBlock } from './GlpCardBlock'
import { GlpFeaturesSectionBlock } from './GlpFeaturesSectionBlock'
import { GlpHeaderBlock } from './GlpHeaderBlock'
import { GlpHeroSectionBlock } from './GlpHeroSectionBlock'
import { GlpRowBlock } from './GlpRowBlock'
import { GlpTextBlock } from './GlpTextBlock'

interface LandingPageBlocksProps {
  blocks?: UpsellBlock[] | null
  slug: string
  landingPage: UpsellLandingPage
  currentUpsellOrigin: {
    request_source: string
    request_source_category?: string
    request_source_component?: string
  } | null
}

/**
 * LandingPageBlocks component renders blocks in the exact order they appear in DatoCMS.
 * The blocks array from the GraphQL query preserves the order defined in DatoCMS modular content,
 * and we render them sequentially using .map() which maintains that order.
 */
export const LandingPageBlocks: FC<LandingPageBlocksProps> = ({
  blocks,
  slug,
  landingPage,
  currentUpsellOrigin,
}) => {
  if (!blocks || blocks.length === 0) {
    return null
  }
  return (
    <>
      {blocks.map((block, index) => {
        if (!block) {
          return null
        }

        // Render blocks in DatoCMS order - the array order matches the CMS order
        if (block.glpButton) {
          return (
            <Box
              key={`glp-button-${block.glpButton.id}-${index}`}
              alignItems="center"
              className="w-fit mx-auto"
            >
              <GlpButtonBlock
                block={block.glpButton}
                slug={slug}
                landingPage={landingPage}
                currentUpsellOrigin={currentUpsellOrigin}
              />
            </Box>
          )
        }

        if (block.glpCard) {
          return (
            <GlpCardBlock key={`glp-card-${block.glpCard.id}-${index}`} block={block.glpCard} />
          )
        }

        if (block.glpRow) {
          return (
            <GlpRowBlock
              key={`glp-row-${block.glpRow.id}-${index}`}
              block={block.glpRow}
              slug={slug}
              landingPage={landingPage}
              currentUpsellOrigin={currentUpsellOrigin}
            />
          )
        }

        if (block.glpHeader) {
          return (
            <GlpHeaderBlock
              key={`glp-header-${block.glpHeader.id}-${index}`}
              block={block.glpHeader}
              slug={slug}
              landingPage={landingPage}
              currentUpsellOrigin={currentUpsellOrigin}
            />
          )
        }

        if (block.glpHeroSection) {
          return (
            <GlpHeroSectionBlock
              key={`glp-hero-section-${block.glpHeroSection.id}-${index}`}
              block={block.glpHeroSection}
              slug={slug}
              landingPage={landingPage}
              currentUpsellOrigin={currentUpsellOrigin}
            />
          )
        }

        if (block.glpFeaturesSection) {
          return (
            <GlpFeaturesSectionBlock
              key={`glp-features-section-${block.glpFeaturesSection.id}-${index}`}
              block={block.glpFeaturesSection}
              slug={slug}
              landingPage={landingPage}
              currentUpsellOrigin={currentUpsellOrigin}
            />
          )
        }

        if (block.glpText) {
          return (
            <GlpTextBlock key={`glp-text-${block.glpText.id}-${index}`} block={block.glpText} />
          )
        }

        return null
      })}
    </>
  )
}
